import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import defaultProductImage from "../../images/Default-Image.jpeg";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import { getUserData } from "../../utils/authUtils";
import { orderService } from "../../api/services/orderService";
import "../OrderSuccess/OrderSuccess.css";
import "./OrderHistoryDetailModal.css";

const ERP_IMAGE_BASE = "https://wireox.erp.gold";

const getOrderLineImageUrl = (order) => {
  const img = order?.Images?.[0];
  const path = img?.ImagePath;
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http")) return path;
  return `${ERP_IMAGE_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

const getItemDetailImageUrl = (item) => {
  const img = item?.Images?.[0];
  const path = img?.ImagePath;
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http")) return path;
  return `${ERP_IMAGE_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
};

const PAYMENT_METHOD_LABEL = "Credit Card (Stripe)";

const paymentMethodLabel = (code) => {
  if (code == null) return PAYMENT_METHOD_LABEL;
  if (code === 1) return PAYMENT_METHOD_LABEL;
  return `Payment method ${code}`;
};

/** Same phone formatting as Checkout ship-to */
const formatPhoneDisplay = (phone) => {
  if (phone == null || phone === "") return null;
  const raw = String(phone).replace(/\D/g, "");
  if (raw.length === 10) {
    return `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`;
  }
  return String(phone);
};

const OrderHistoryDetailModal = ({ order, onClose, onBuyAgain }) => {
  const showPrices = useCanViewPrices();
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    document.body.classList.add("ohd-print-active");
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("ohd-print-active");
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    if (!order) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);

    const txnNumber = order.TxnNumber || order.TxnId;
    const userData = getUserData();
    const userName = userData?.email || userData?.userName;

    if (!txnNumber || !userName) {
      setDetailLoading(false);
      setDetailError(
        !txnNumber ? "This order has no transaction number." : "You must be logged in to load order details."
      );
      return;
    }

    orderService
      .getOrderDetail(txnNumber, userName)
      .then((res) => {
        if (cancelled) return;
        if (res?.Success && res?.Data) {
          setDetail(res.Data);
          setDetailError(null);
        } else {
          setDetail(null);
          setDetailError(res?.ErrorMessage || "Could not load order details.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setDetail(null);
        setDetailError(
          err?.response?.data?.ErrorMessage ||
            err?.response?.data?.message ||
            err?.message ||
            "Could not load order details."
        );
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [order]);

  const lineItems = useMemo(() => {
    if (!order) return [];
    const rows = Array.isArray(detail?.ItemDetail) ? detail.ItemDetail : [];
    if (rows.length > 0) {
      return rows.map((row) => ({
        key: row.ItemId ?? row.ItemCode,
        name: row.ItemName || "Item",
        sku: row.ItemCode,
        qty: Number(row.Quantity ?? 1),
        unitPrice: Number(row.UnitPrice ?? 0),
        lineTotal: Number(
          row.TotalPrice ?? Number(row.UnitPrice ?? 0) * Number(row.Quantity ?? 1)
        ),
        image: getItemDetailImageUrl(row) || defaultProductImage,
      }));
    }
    const imgSrc = getOrderLineImageUrl(order) || defaultProductImage;
    const unitPrice = Number(order.TotalLineItemPrice ?? 0);
    const qty = 1;
    const lineTotal = Number(order.TotalLineItemPrice ?? unitPrice * qty);
    return [
      {
        key: order.ID ?? "line",
        name: order.ItemName || "Item",
        sku: null,
        qty,
        unitPrice,
        lineTotal,
        image: imgSrc,
      },
    ];
  }, [detail, order]);

  if (!order) return null;

  const txnDisplay = detail?.TxnId || order.TxnNumber || order.TxnId || "—";
  const orderDateRaw = detail?.OrderDate || order.DocDate;
  const orderDate = orderDateRaw
    ? new Date(orderDateRaw).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";
  const customerName = detail?.CustomerName || order.CustomerName || "—";

  const subtotal = detail
    ? Number(detail.TotalAmount ?? 0) -
      Number(detail.TaxAmount ?? 0) -
      Number(detail.ShippingAmount ?? 0)
    : Number(order.SubTotal ?? 0);
  const shipping = detail ? Number(detail.ShippingAmount ?? 0) : Number(order.Freight ?? 0);
  const taxes = detail ? Number(detail.TaxAmount ?? 0) : Number(order.SalesTaxAmount ?? 0);
  const total = detail ? Number(detail.TotalAmount ?? 0) : Number(order.TotalAmount ?? 0);

  const shippingDetail = detail?.ShippingDetail;
  const billingDetail = detail?.BillingDetail;

  const shippingBlock = detailLoading ? (
    <span className="ohd-info-placeholder">—</span>
  ) : shippingDetail ? (
    <>
      {shippingDetail.RecipientName || "—"}
      <br />
      {[
        shippingDetail.StreetAddress,
        [shippingDetail.City, shippingDetail.State, shippingDetail.PostalCode].filter(Boolean).join(", "),
      ]
        .filter(Boolean)
        .join(", ") || "—"}
      <br />
      {shippingDetail.Country || "—"}
      {formatPhoneDisplay(shippingDetail.PhoneNumber) && (
        <>
          <br />
          T: {formatPhoneDisplay(shippingDetail.PhoneNumber)}
        </>
      )}
    </>
  ) : (
    <>
      {customerName}
      <br />
      <span className="ohd-info-placeholder">Shipping address not available.</span>
    </>
  );

  const billingBlock = detailLoading ? (
    <span className="ohd-info-placeholder">—</span>
  ) : billingDetail ? (
    <>
      {billingDetail.RecipientName || "—"}
      <br />
      {billingDetail.StreetAddress || "—"}
      <br />
      {[billingDetail.City, billingDetail.State, billingDetail.PostalCode].filter(Boolean).join(", ") || "—"}
      <br />
      {billingDetail.Country || "—"}
      {billingDetail.PhoneNumber && (
        <>
          <br />
          T: {formatPhoneDisplay(billingDetail.PhoneNumber) || billingDetail.PhoneNumber}
        </>
      )}
    </>
  ) : (
    <span className="ohd-info-placeholder">Billing address not available.</span>
  );

  const modal = (
    <div className="ohd-overlay" onClick={onClose} role="presentation">
      <div
        className="order-success-page ohd-modal-wrap"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ohd-order-title"
      >
        <div className="order-success-card ohd-card">
          <button
            type="button"
            className="ohd-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          <h1 id="ohd-order-title" className="order-success-title">
            Order details
          </h1>

          <div className="order-success-header-row">
            <div className="order-success-meta">
              <span className="order-success-id">Order #{txnDisplay}</span>
              <span className="order-success-status order-success-status-pending">
                Completed
              </span>
              <span className="order-success-date">{orderDate}</span>
            </div>
            <div className="order-success-actions">
              <button
                type="button"
                className="order-success-btn order-success-btn-reorder"
                onClick={() => onBuyAgain?.(order)}
              >
                Buy Again
              </button>
              <button
                type="button"
                className="order-success-btn order-success-btn-print"
                onClick={() => window.print()}
              >
                Print Order
              </button>
            </div>
          </div>

          {detailError && !detailLoading && (
            <p className="ohd-info-loading" role="alert">
              {detailError}
            </p>
          )}

          <section className="order-success-section">
            <h2 className="order-success-section-title">Order Details</h2>
            <div className="order-success-details-wrap">
              <div className="order-success-table-wrap">
                <table className="order-success-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      {showPrices && <th>Price</th>}
                      <th>Qty</th>
                      {showPrices && <th>Subtotal</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <div className="order-success-product-cell">
                            <div className="order-success-product-img">
                              <img
                                src={row.image}
                                alt=""
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = defaultProductImage;
                                }}
                              />
                            </div>
                            <div>
                              <span className="order-success-product-name">{row.name}</span>
                              {row.sku && (
                                <span className="order-success-product-sku">SKU: {row.sku}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        {showPrices && <td>${row.unitPrice.toFixed(2)}</td>}
                        <td>{row.qty}</td>
                        {showPrices && <td>${row.lineTotal.toFixed(2)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="order-success-totals">
                {showPrices && (
                  <>
                    <div className="order-success-total-row">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="order-success-total-row">
                      <span>Shipping</span>
                      <span>${shipping.toFixed(2)}</span>
                    </div>
                    <div className="order-success-total-row">
                      <span>Taxes</span>
                      <span>${taxes.toFixed(2)}</span>
                    </div>
                    <div className="order-success-total-row order-success-total-final">
                      <span>Total</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="order-success-section">
            <h2 className="order-success-section-title">Order Information</h2>
            {detailLoading && (
              <p className="ohd-info-loading">Loading order details…</p>
            )}
            <div className="order-success-info-grid">
              <div className="order-success-info-block">
                <h3 className="order-success-info-label">Shipping Address</h3>
                <p className="order-success-info-text">{shippingBlock}</p>
              </div>
              <div className="order-success-info-block">
                <h3 className="order-success-info-label">Shipping Method</h3>
                <p className="order-success-info-text">Standard Shipping</p>
              </div>
              <div className="order-success-info-block">
                <h3 className="order-success-info-label">Billing Address</h3>
                <p className="order-success-info-text">{billingBlock}</p>
              </div>
              <div className="order-success-info-block">
                <h3 className="order-success-info-label">Payment Method</h3>
                <p className="order-success-info-text">
                  {detail ? paymentMethodLabel(detail.PaymentMethod) : PAYMENT_METHOD_LABEL}
                </p>
              </div>
            </div>
          </section>

          <div className="order-success-footer-actions">
            <button
              type="button"
              className="order-success-btn order-success-btn-primary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default OrderHistoryDetailModal;
